import React from 'react';
import { useParams } from 'react-router-dom';
import { Text } from '@perch-labs/shrike';
import { formatWithCommas } from '@perch-labs/warbler';
import { range, zipWith } from 'lodash-es';
import SVG from 'react-inlinesvg';
import Graphic from '/images/graphics/tree-growing-2.svg';
import FactoryIcon from '/images/icons/factory.svg';
import GasesIcon from '/images/icons/gases.svg';
import IceburgIcon from '/images/icons/iceburg.svg';
import { copyFor, routes } from 'config';
import { LoadingPage } from 'components/loading';
import { ErrorPage } from 'components/errors';
import { BackLink } from 'components/back-link';
import { MultipartDescription } from 'components/multipart-description';
import { useImpactStats } from 'hooks/use-impact-stats.hook';
import { MAX_SECONDARY_PAGE_WIDTH } from 'domain/style';
import { Container, Hr, Page, Sources, SourceText } from 'styles';
import { Banner } from './impact.style';

const getCopy = copyFor('usage.impact');
const getBackLinkCopy = copyFor('navigation.backLink.dashboardLocations');

interface Props {
  location?: any;
}

export const Impact = (props: Props) => {
  const { planId } = useParams<{ planId?: string }>();
  const { usage } = routes({ planId });
  const previousAppLocation = props.location.state?.from;
  const {
    data: impactStats,
    error,
    loading,
  } = useImpactStats({ planId: Number(planId) });

  const paragraphs = getCopy('content.paragraphs').map(paragraph => (
    <Text
      key={paragraph.slice(0, 10)}
      textStyle="paragraph500"
      margin={{ bottom: '16px' }}
    >
      {paragraph}
    </Text>
  ));
  const [firstParagraph, ...otherParagraphs] = paragraphs;

  if (loading) return <LoadingPage />;
  if (error || !impactStats) {
    return <ErrorPage title={getCopy('title')} to={usage} />;
  }

  const kwh = formatWithCommas(impactStats.carbonEquivalency.kwh, 0, 0);
  const totalCarbon = formatWithCommas(
    impactStats.carbonEquivalency.totalCarbon,
    0,
    0
  );

  const getStats = () => (
    <Container>
      <Text textStyle="heading900" color="primaryInverse">
        {totalCarbon}
      </Text>
      <Text color="primaryInverse">{getCopy('banner.description')}</Text>
    </Container>
  );

  const ids = range(3);
  const icons = [FactoryIcon, GasesIcon, IceburgIcon];
  const descriptions = getCopy('content.captions').map(caption => (
    <Text textStyle="heading500">{caption}</Text>
  ));
  const parts = zipWith(
    ids,
    icons,
    descriptions,
    (id: number, icon: string, description: React.ReactNode) => ({
      description,
      icon,
      id,
    })
  );

  return (
    <>
      <BackLink
        to={previousAppLocation || usage}
        location={getBackLinkCopy('usage')}
      />
      <Page maxWidth={MAX_SECONDARY_PAGE_WIDTH}>
        <Text textStyle="heading800" tag="h1" margin={{ bottom: '16px' }}>
          {getCopy('title')}
        </Text>
        <Text>{getCopy('description', { kwh, totalCarbon })}</Text>
        <Banner>
          {getStats()}
          <SVG src={Graphic} height="216px" width="111px" />
        </Banner>
        {firstParagraph}
        <MultipartDescription
          parts={parts}
          margin={{ bottom: '32px', top: '32px' }}
        />
        {otherParagraphs}
        <Hr />
        <Sources>
          {getCopy('sources').map(item => (
            <SourceText key={item}>{item}</SourceText>
          ))}
        </Sources>
      </Page>
    </>
  );
};
